// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing here
// leaves the device — the AI stash recap (brain.ts) only ever sends the DERIVED-SIGNALS
// object computed in Rust (signals.rs), never these raw rows.
//
// Domain: a yarn stash. Three record types — skeins (the stash), patterns (the queue),
// projects (the finished log). Yardage is the load-bearing unit; dye-lot + grams-remaining
// are the moat (partial-skein remainders stay honest).

import { invoke } from "./tauri-web";

// ── Skein ────────────────────────────────────────────────────────────────────
export interface Skein {
  id: string;
  brand: string;
  colorway: string;
  fiber: string;
  /** Yarn weight class: lace, fingering, sport, dk, worsted, aran, bulky, super-bulky, jumbo. */
  weight: string;
  yardage_per_skein: number;
  grams_per_skein: number;
  skein_count: number;
  /** Grams left across all units — the partial-use truth behind usable yardage. */
  grams_remaining: number;
  dye_lot: string;
  bought_where: string;
  bought_on: string; // "YYYY-MM-DD" or ""
  price: number;
  currency: string;
  notes: string;
  created_at: string;
  updated_at: string;
  /** Downscaled JPEG data-URL of a photo of this yarn, ON-DEVICE only. "" when unset. The model
   *  never sees it — a photo filed through chat is an attachment, not vision. */
  photo: string;
}
export interface NewSkein {
  brand: string;
  colorway?: string;
  fiber?: string;
  weight?: string;
  yardage_per_skein?: number;
  grams_per_skein?: number;
  skein_count?: number;
  grams_remaining?: number;
  dye_lot?: string;
  bought_where?: string;
  bought_on?: string;
  price?: number;
  currency?: string;
  notes?: string;
}
export type SkeinPatch = Partial<Omit<NewSkein, "brand"> & { brand: string }>;

// ── Pattern ──────────────────────────────────────────────────────────────────
export type PatternStatus = "queued" | "making" | "done";
export interface Pattern {
  id: string;
  name: string;
  yarn_weight: string;
  total_yardage: number;
  needle_size: string;
  status: PatternStatus;
  source: string;
  notes: string;
  created_at: string;
  updated_at: string;
}
export interface NewPattern {
  name: string;
  yarn_weight?: string;
  total_yardage?: number;
  needle_size?: string;
  status?: PatternStatus;
  source?: string;
  notes?: string;
}
export type PatternPatch = Partial<Omit<NewPattern, "name"> & { name: string }>;

// ── Project (finished log) ─────────────────────────────────────────────────────
export interface Project {
  id: string;
  name: string;
  yarn_weight: string;
  yardage_used: number;
  finished_on: string; // "YYYY-MM-DD" or ""
  notes: string;
  created_at: string;
  updated_at: string;
}
export interface NewProject {
  name: string;
  yarn_weight?: string;
  yardage_used?: number;
  finished_on?: string;
  notes?: string;
}
export type ProjectPatch = Partial<Omit<NewProject, "name"> & { name: string }>;

// ── Derived signals (computed on-device in Rust; only aggregates go to the brain) ──
// Mirrors src-tauri/src/signals.rs::StashSignals. Every field is a deterministic
// aggregate — sums, usable-yardage (partial-skein aware), pattern surplus/shortfall,
// dye-lot risk — never a raw row. This is the payload the AI recap narrates (brain.ts).
export interface Totals {
  skein_lines: number;
  physical_skeins: number;
  label_yardage: number;
  usable_yardage: number;
  partial_lines: number;
  queued_patterns: number;
  making_patterns: number;
  total_spend: SpendTotal | null;
}
export interface SpendTotal {
  amount: number;
  currency: string;
  single_currency: boolean;
  priced_lines: number;
}
export interface WeightBucket {
  weight: string;
  skein_lines: number;
  physical_skeins: number;
  usable_yardage: number;
  typical_skein_yardage: number | null;
}
export interface FiberBucket {
  fiber: string;
  skein_lines: number;
  usable_yardage: number;
  share_pct: number;
}
export interface ColorwayBucket {
  colorway: string;
  skein_lines: number;
  usable_yardage: number;
}
export type Fit = "covered" | "short" | "unknown";
export interface PatternFit {
  pattern: string;
  status: string;
  yarn_weight: string;
  needs_yardage: number;
  stash_yardage_same_weight: number;
  delta: number;
  fit: Fit;
  skeins_short: number | null;
  typical_skein_yardage: number | null;
}
export interface DyeLotAlert {
  brand: string;
  colorway: string;
  distinct_lots: number;
  lots: string[];
  usable_yardage: number;
}
export interface Idle {
  oldest_purchase: string | null;
  lines_over_one_year: number;
  usable_yardage_over_one_year: number;
}
export interface ProjectsSummary {
  finished_count: number;
  total_yardage_used: number;
  last_finished_on: string | null;
  avg_yardage_per_project: number | null;
}
export interface StashSignals {
  schema: string;
  generated_for: string;
  totals: Totals;
  by_weight: WeightBucket[];
  by_fiber: FiberBucket[];
  colorways_top: ColorwayBucket[];
  pattern_fit: PatternFit[];
  dye_lot_alerts: DyeLotAlert[];
  idle: Idle;
  projects: ProjectsSummary;
}

export const store = {
  // skeins
  listSkeins: () => invoke<Skein[]>("list_skeins"),
  getSkein: (id: string) => invoke<Skein | null>("get_skein", { id }),
  createSkein: (input: NewSkein) => invoke<Skein>("create_skein", { input }),
  updateSkein: (id: string, patch: SkeinPatch) => invoke<void>("update_skein", { id, patch }),
  /** Attach a photo (downscaled JPEG data-URL) to a skein — filed ON-DEVICE from chat (a photo of the
   *  yarn). Never leaves the device, never sent to the model. "" clears it. */
  setSkeinPhoto: (id: string, photo: string) => invoke<void>("set_skein_photo", { id, photo }),
  deleteSkein: (id: string) => invoke<void>("delete_skein", { id }),
  // patterns
  listPatterns: () => invoke<Pattern[]>("list_patterns"),
  getPattern: (id: string) => invoke<Pattern | null>("get_pattern", { id }),
  createPattern: (input: NewPattern) => invoke<Pattern>("create_pattern", { input }),
  updatePattern: (id: string, patch: PatternPatch) => invoke<void>("update_pattern", { id, patch }),
  deletePattern: (id: string) => invoke<void>("delete_pattern", { id }),
  // projects
  listProjects: () => invoke<Project[]>("list_projects"),
  getProject: (id: string) => invoke<Project | null>("get_project", { id }),
  createProject: (input: NewProject) => invoke<Project>("create_project", { input }),
  updateProject: (id: string, patch: ProjectPatch) => invoke<void>("update_project", { id, patch }),
  deleteProject: (id: string) => invoke<void>("delete_project", { id }),
  /** Compute the deterministic stash-signals object on-device. `today` = client local YYYY-MM-DD. */
  computeSignals: (today: string) => invoke<StashSignals>("compute_signals", { today }),
  // row counters
  listCounters: () => invoke<Counter[]>("list_counters"),
  createCounter: (input: { name?: string; step?: number }) => invoke<Counter>("create_counter", { input }),
  setCounterCount: (id: string, count: number) => invoke<void>("set_counter_count", { id, count }),
  updateCounter: (id: string, name?: string, step?: number) => invoke<void>("update_counter", { id, name, step }),
  deleteCounter: (id: string) => invoke<void>("delete_counter", { id }),
};

// ── Row counter (knitting/crochet table-stake) ──
export interface Counter {
  id: string;
  name: string;
  count: number;
  step: number;
  created_at: string;
  updated_at: string;
}

// ── Domain reference data (UI helpers — no advice, just normalization/labels) ──

/** Canonical yarn-weight classes (Craft Yarn Council scale), thinnest → thickest. */
export const YARN_WEIGHTS = [
  "lace", "fingering", "sport", "dk", "worsted", "aran", "bulky", "super-bulky", "jumbo",
] as const;
export type YarnWeight = (typeof YARN_WEIGHTS)[number];

export const WEIGHT_LABEL: Record<string, string> = {
  lace: "Lace (0)",
  fingering: "Fingering (1)",
  sport: "Sport (2)",
  dk: "DK (3)",
  worsted: "Worsted (4)",
  aran: "Aran (5)",
  bulky: "Bulky (6)",
  "super-bulky": "Super Bulky (7)",
  jumbo: "Jumbo (8)",
  unspecified: "Unspecified",
};

/** Human label for a stored weight code (falls back to title-case of the raw value). */
export function weightLabel(w: string): string {
  if (!w) return "Unspecified";
  return WEIGHT_LABEL[w] ?? w.charAt(0).toUpperCase() + w.slice(1);
}

/** Curated fiber suggestions; user can also type their own. */
export const COMMON_FIBERS = [
  "merino wool", "wool", "cotton", "acrylic", "alpaca", "cashmere",
  "mohair", "silk", "linen", "bamboo", "nylon blend", "wool blend",
];

/** Currencies offered in the picker; user can type any 3-letter code too. */
export const COMMON_CURRENCIES = ["USD", "GBP", "EUR", "CAD", "AUD", "JPY"];

export const todayLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
};

/** Format a yardage figure with thousands separators (e.g. 1180 → "1,180"). */
export function fmtYds(y: number): string {
  return Math.round(y).toLocaleString("en-US");
}

/** Usable yardage for a single stash line, partial-skein aware. Mirrors signals.rs::usable_yardage
 *  (used in the UI list so each row shows the honest figure, not the label). */
export function usableYardage(s: Skein): number {
  const labelTotal = s.yardage_per_skein * Math.max(0, s.skein_count);
  if (s.grams_per_skein > 0 && s.grams_remaining >= 0) {
    const yardsPerGram = s.yardage_per_skein / s.grams_per_skein;
    return Math.max(0, Math.min(yardsPerGram * s.grams_remaining, Math.max(0, labelTotal)));
  }
  return Math.max(0, labelTotal);
}

/** Is this line a partial (grams known and meaningfully below a full stash)? */
export function isPartial(s: Skein): boolean {
  if (s.grams_per_skein <= 0 || s.grams_remaining <= 0) return false;
  const full = s.grams_per_skein * Math.max(0, s.skein_count);
  return s.grams_remaining + 0.5 < full;
}
